/**
 * ConsoleCapture — Singleton ring buffer a console error/warn üzenetekhez.
 * Inicializálás: main.tsx-ben, app bootstrap-kor.
 * Ticket beküldéskor az utolsó N entry-t csatoljuk.
 */

export interface ConsoleEntry {
  level: 'error' | 'warn';
  message: string;
  source?: string;
  line?: number;
  stack?: string;
  timestamp: number;
}

const MAX_ENTRIES = 50;

class ConsoleCaptureService {
  private entries: ConsoleEntry[] = [];
  private initialized = false;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // 1. Unhandled JS errors
    window.addEventListener('error', (event: ErrorEvent) => {
      this.push({
        level: 'error',
        message: event.message || 'Unknown error',
        source: event.filename,
        line: event.lineno,
        stack: event.error?.stack?.substring(0, 500),
        timestamp: Date.now(),
      });
    });

    // 2. Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      this.push({
        level: 'error',
        message: reason?.message || String(reason) || 'Unhandled rejection',
        stack: reason?.stack?.substring(0, 500),
        timestamp: Date.now(),
      });
    });

    // 3. Override console.error
    const origError = console.error;
    console.error = (...args: unknown[]) => {
      this.push({
        level: 'error',
        message: args.map(String).join(' ').substring(0, 300),
        timestamp: Date.now(),
      });
      origError.apply(console, args);
    };

    // 4. Override console.warn
    const origWarn = console.warn;
    console.warn = (...args: unknown[]) => {
      this.push({
        level: 'warn',
        message: args.map(String).join(' ').substring(0, 300),
        timestamp: Date.now(),
      });
      origWarn.apply(console, args);
    };
  }

  private push(entry: ConsoleEntry): void {
    this.entries.push(entry);
    if (this.entries.length > MAX_ENTRIES) {
      this.entries.shift();
    }
  }

  /** Utolsó N entry lekérdezése (ticket csatoláshoz) */
  getRecent(limit = 10): ConsoleEntry[] {
    return this.entries.slice(-limit);
  }

  /** Összes error (nem warn) száma — badge-hez */
  getErrorCount(): number {
    return this.entries.filter(e => e.level === 'error').length;
  }

  /** Utolsó lekérdezés óta keletkezett error-ök száma */
  getErrorCountSince(timestamp: number): number {
    return this.entries.filter(e => e.level === 'error' && e.timestamp > timestamp).length;
  }

  clear(): void {
    this.entries = [];
  }
}

export const consoleCapture = new ConsoleCaptureService();
